/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

class MarqueeTicker extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector(".marquee-ticker__track");
    if (!this.track) return;

    if (this.hasAttribute("pause-on-hover")) {
      this.addEventListener("mouseenter", this.pause.bind(this));
      this.addEventListener("mouseleave", this.resume.bind(this));
    }

    if (this.hasAttribute("reveal-on-scroll")) {
      this._setupRevealOnScroll();
    } else {
      this._startAnimation();
    }
  }

  _setupRevealOnScroll() {
    if (!window.IntersectionObserver) {
      this._startAnimation();
      return;
    }
    this.track.style.animationPlayState = "paused";
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._startAnimation();
          } else {
            this.track.style.animationPlayState = "paused";
          }
        });
      },
      { rootMargin: "50px 0px", threshold: 0 },
    );
    observer.observe(this);
    this._observer = observer;
  }

  _startAnimation() {
    if (this.track) {
      this.track.style.animationPlayState = "running";
    }
  }

  pause() {
    if (this.track) {
      this.track.style.animationPlayState = "paused";
    }
  }

  resume() {
    if (this.track) {
      this.track.style.animationPlayState = "running";
    }
  }

  disconnectedCallback() {
    this._observer?.disconnect();
  }
}
customElements.define("marquee-ticker", MarqueeTicker);

class InfoAccordion extends HTMLElement {
  connectedCallback() {
    this.toggleButton = this.querySelector(".icon-text");
    this.content = this.querySelector(".info-accordion__content");
    this.toggleButton.addEventListener("click", this.toggle.bind(this));
  }

  toggle() {
    this.isExpanded = !this.isExpanded;
    this.toggleButton.setAttribute("aria-expanded", this.isExpanded);
    this.content.setAttribute("aria-expanded", this.isExpanded);
  }
}

customElements.define("info-accordion", InfoAccordion);

class ProductUpsells extends HTMLElement {
  connectedCallback() {
    this.swiperConfig = JSON.parse(this.getAttribute("data-swiper-config"));
    this.fetchProducts();
  }

  get productId() {
    return this.getAttribute("data-product-id");
  }

  fetchProducts() {
    const url =
      window.Shopify.routes.root +
      `recommendations/products?product_id=${this.productId}&limit=5&section_id=product-upsells&intent=related`;

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        html
          .querySelector(".swiper")
          .setAttribute(
            "data-swiper-config",
            JSON.stringify(this.swiperConfig),
          );
        this.querySelector(".product-upsells__container").innerHTML =
          html.querySelector(".shopify-section").innerHTML;
      });
  }
}

customElements.define("product-upsells", ProductUpsells);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.swiperConfig = JSON.parse(this.getAttribute("data-swiper-config"));
  }

  connectedCallback() {
    this.swiper = new Swiper(this, this.swiperConfig);
  }
}

customElements.define("slider-component", SliderComponent);

class AtcForm extends HTMLFormElement {
  connectedCallback() {
    this.submitButton = this.querySelector("button[type='submit']");
    this.addEventListener("submit", this.submit.bind(this));
  }

  submit(event) {
    event.preventDefault();
    this.submitButton.setAttribute("disabled", "disabled");
    this.submitButton.setAttribute("aria-busy", "true");
    const formData = new FormData(this);
    const url = window.Shopify.routes.root + `cart/add.js`;
    fetch(url, {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then(() => {
        this.submitButton.removeAttribute("disabled");
        this.submitButton.removeAttribute("aria-busy");

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
          }),
        );

        document.getElementById("mini-cart").open = true;
      })
      .catch((error) => {
        console.error(error);
      });
  }
}

customElements.define("atc-form", AtcForm, { extends: "form" });

class ProductConnector extends HTMLElement {
  constructor() {
    super();
  }

  get sectionId() {
    return this.getAttribute("data-section-id");
  }

  get optionName() {
    return this.getAttribute("data-option");
  }

  connectedCallback() {
    this.selectors = this.querySelectorAll("input");
    this.variantJson = JSON.parse(
      document.querySelector("script[data-variant]").innerHTML,
    );

    this.selectors.forEach((selector) => {
      selector.addEventListener("change", this.rerenderProduct.bind(this));
    });
  }

  findVariantByOptions(product, selectedOptions) {
    if (!product?.variants?.length) return undefined;
    const skipOption = this.optionName;
    
    return product.variants.find((v) => {
      return [1, 2, 3].every((i) => {
        const key = `option${i}`;
        if (key === skipOption) return true;
        const selected = selectedOptions[key];
        const variantVal = v[key];
        return selected == null
          ? variantVal == null || variantVal === ""
          : variantVal === selected;
      });
    });
  }

  rerenderProduct(event) {
    const productHandle = event.target.value;
    const section = this.closest(".shopify-section");

    console.log(this.variantJson);

    const selectedOptions = {
      option1: this.variantJson?.option1 ?? null,
      option2: this.variantJson?.option2 ?? null,
      option3: this.variantJson?.option3 ?? null,
    };

    const productJsonUrl = `${window.Shopify.routes.root}products/${productHandle}.js`;

    fetch(productJsonUrl)
      .then((response) => response.json())
      .then((newProduct) => {
        const matchingVariant = this.findVariantByOptions(
          newProduct,
          selectedOptions,
        );
        const sectionUrl = `${window.Shopify.routes.root}products/${productHandle}?section_id=${this.sectionId}${matchingVariant?.id ? `&variant=${matchingVariant.id}` : ""}`;

        return fetch(sectionUrl)
          .then((res) => res.text())
          .then((html) => ({ html, variantId: matchingVariant?.id }));
      })
      .then(({ html, variantId }) => {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;

        const newSection = tempDiv.querySelector(".shopify-section");
        if (newSection) section.innerHTML = newSection.innerHTML;

        const newUrl = new URL(
          `${window.Shopify.routes.root}products/${productHandle}`,
          window.location.origin,
        );
        if (variantId) newUrl.searchParams.set("variant", String(variantId));
        window.history.replaceState({}, "", newUrl.toString());
      });
  }
}

customElements.define("product-connector", ProductConnector);

class ComparisonSliderComponent extends HTMLElement {
  constructor() {
    super();
    this._boundSync = this.sync.bind(this);
  }

  connectedCallback() {
    this.mediaWrapper = this.querySelector("[data-comparison-media-wrapper]");
    this.slider = this.querySelector(".cs-slider");
    if (!this.mediaWrapper || !this.slider) return;

    this.sync();
    this.slider.addEventListener("input", this._boundSync);
    this.slider.addEventListener("pointerdown", this._boundSync);
  }

  disconnectedCallback() {
    if (this.slider) {
      this.slider.removeEventListener("input", this._boundSync);
      this.slider.removeEventListener("pointerdown", this._boundSync);
    }
  }

  sync() {
    if (!this.mediaWrapper || !this.slider) return;
    const min = Number(this.slider.min);
    const max = Number(this.slider.max);
    const val = (Number(this.slider.value) - min) / (max - min);
    const compareValue = Math.round(val * 100);
    this.mediaWrapper.style.setProperty("--compare", String(compareValue));
  }
}

if (!customElements.get("comparison-slider-component")) {
  customElements.define(
    "comparison-slider-component",
    ComparisonSliderComponent,
  );
}

class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.countdownDate = Number(this.dataset.date) * 1000;
    this.daysElement = this.querySelector("#CountdownDays");
    this.hoursElement = this.querySelector("#CountdownHours");
    this.minutesElement = this.querySelector("#CountdownMinutes");
    this.secondsElement = this.querySelector("#CountdownSeconds");
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.countdownInterval = setInterval(() => {
      this.updateCountdown();
    }, 1000);
  }

  updateCountdown() {
    const now = new Date().getTime();
    const timeDifference = this.countdownDate - now;

    if (timeDifference < 0) {
      clearInterval(this.countdownInterval);
      this.daysElement.innerText = "00";
      this.hoursElement.innerText = "00";
      this.minutesElement.innerText = "00";
      this.secondsElement.innerText = "00";
      return;
    }

    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor(
      (timeDifference % (1000 * 60 * 60)) / (1000 * 60)
    );
    const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

    this.daysElement.innerText = this.formatTime(days);
    this.hoursElement.innerText = this.formatTime(hours);
    this.minutesElement.innerText = this.formatTime(minutes);
    this.secondsElement.innerText = this.formatTime(seconds);
  }

  formatTime(timeUnit) {
    return timeUnit < 10 ? "0" + timeUnit : timeUnit;
  }
}

customElements.define("countdown-timer", CountdownTimer);
